/**
 * Pure helpers for the CB-8.3 Settings → Contracts sending-identity surface (FR-72 / FR-74).
 *
 * Two concerns, both pure so they can be unit-tested without a DOM or network:
 *   1. The contract sending FROM-address that CB-4.1 sends from — `contracts@{tenant_domain}`,
 *      defaulting to the platform domain when the tenant has no custom domain.
 *   2. Merge-field guardrails for the editable `contract_invite` template — which `{placeholder}`
 *      tokens are REQUIRED (chiefly `{signing_link}`, the per-recipient token URL CB-4.1 mints).
 *      Editing must WARN (never hard-block) when a required placeholder is removed (Task 3).
 */

/** Platform default email domain when a tenant has no custom domain (mirrors EmailDomainConfiguration). */
export const DEFAULT_EMAIL_DOMAIN = 'mail.joulescrm.com';

/**
 * Required merge fields for the invite template. `{signing_link}` is load-bearing — without it the
 * recipient has no way to reach the ceremony. The rest of the FR-38 set is optional copy.
 */
export const CONTRACT_INVITE_REQUIRED_VARS = ['signing_link'] as const;

/** The contract sending FROM-address CB-4.1 uses: `contracts@{domain}` (default domain when unset). */
export function resolveContractFromAddress(emailDomain: string | null | undefined): string {
  const domain = (emailDomain || '').trim() || DEFAULT_EMAIL_DOMAIN;
  return `contracts@${domain}`;
}

/**
 * Which required merge fields are MISSING from the edited template. Scans subject + body for the
 * literal `{var}` token. Returns the bare variable names (e.g. `['signing_link']`) so the caller can
 * render a non-blocking warning. Empty array = all required placeholders preserved.
 */
export function findMissingRequiredMergeFields(
  subject: string,
  body: string,
  required: readonly string[] = CONTRACT_INVITE_REQUIRED_VARS
): string[] {
  const haystack = `${subject || ''}\n${body || ''}`;
  return required.filter((v) => !haystack.includes(`{${v}}`));
}
